import { filterToParams, getFilterOrder } from '../../../hooks/useDataList';

import { FILTER } from '../../../components/admin/curator/CuratorFilter/CuratorFilter';
import { createSlice } from '@reduxjs/toolkit';
import { history } from '../../../history';
import { isEqual } from 'lodash';
import { syncFiltersIntoQuery } from '../../../helpers/urlHelper';

const sidebar = createSlice({
  name: 'sidebar',
  initialState: {
    labels: [],
    query: [],
    labelCheck: [],
    filtersData: null,
    filterValues: getFilterValuesFromSearchQuery(location.search),
    doFilterUpdateValue: false,
    filtersByKey: {},
    maxVisibleFilterOptions: 3,
    openFilterModal: false,
    showTeamInModal: false,
    clickedSidefilterType: null,
  },
  reducers: {
    setFilterModalStatus: (state, { payload }) => {
      state.openFilterModal = payload;
      if (!payload) {
        state.showTeamInModal = false;
      }
    },
    setShowTeamInModalStatus: (state, { payload }) => {
      state.showTeamInModal = payload;
    },
    setLables: (state, { payload }) => {
      const { sidefilter, maxVisible = 3 } = payload;
      state.labels = sidefilter; // seems like we need to remove it
      state.filtersData = sidefilter;
      state.maxVisibleFilterOptions = maxVisible;
      state.clickedSidefilterType = null;
      state.doFilterUpdateValue = false;
    },
    setFilters: (state, { payload }) => {
      state.filtersByKey[payload.key] = payload.filters;
    },
    setQueryFilter: (state, { payload }) => {
      state.query = payload;
    },
    setLabelCheck: (state, { payload }) => {
      state.labelCheck = payload;
    },
    setFilterValue: (state, { payload }) => {
      state.filterValues = payload;
    },
    setDoFilterUpdateValue: (state, { payload }) => {
      state.doFilterUpdateValue = payload;
    },
    setFilterValueFromSearchQuery: (state, { payload }) => {
      if (isEqual(state.filterValues, payload)) return;
      state.filterValues = payload;
    },
    changeSideFilter: (state, { payload }) => {
      const { baseOrder } = getFilterOrder(state.filterValues);

      const objOrder = baseOrder.reduce((acc, item) => {
        acc[item.name] = item.order;
        return acc;
      }, {});

      // debugger;
      let newFilters = state.filterValues.includes(payload)
        ? state.filterValues.filter((x) => x !== payload)
        : [...state.filterValues, payload];

      const orderedFilters = newFilters.sort((a, b) => {
        const [aName] = a.split('_');
        const [bName] = b.split('_');
        const aOrder = objOrder[`${aName}s`];
        const bOrder = objOrder[`${bName}s`];

        return aOrder - bOrder;
      });

      state.filterValues = orderedFilters;

      syncFilterValues(state.filterValues);
    },
    resetFilters: (state, { payload = { syncQuery: true } }) => {
      if (!window.$restrictFilter) {
        state.filtersData = null;
        state.filterValues = [];
        if (payload.syncQuery) {
          syncFilterValues(state.filterValues);
        }
      }
    },
    afterAddToCollection: (state, { payload }) => {
      if (!state.filtersData?.collections) return;

      const filteredCollections = payload.filter((c) => {
        const exist = state.filtersData?.collections?.find((collection) => collection.id === c.id);
        return !exist;
      });

      state.filtersData?.collections.push(
        ...filteredCollections.map((c) => ({
          id: c.id,
          name: c.name,
          description: c.description,
        }))
      );
    },
    setClickedSidefilterType: (state, { payload }) => {
      if (payload && payload?.includes('_')) {
        const arr = payload.split('_');
        state.clickedSidefilterType = {
          id: +arr[1],
          type: arr[0],
        };
      }
    },
  },
});

export const {
  setLables,
  setQueryFilter,
  setLabelCheck,
  changeSideFilter,
  resetFilters,
  setFilters,
  afterAddToCollection,
  setFilterValue,
  setDoFilterUpdateValue,
  setFilterValueFromSearchQuery,
  setFilterModalStatus,
  setShowTeamInModalStatus,
  setClickedSidefilterType,
} = sidebar.actions;
export const sidebarActions = sidebar.actions
export const sidebarSelector = (state) => state.sidebar;
export const sidebarReducer = sidebar.reducer;
export default sidebarReducer;

export const selectMaxVisibleFilterOptions = (state) => state.sidebar.maxVisibleFilterOptions;
export const selectFilteroptions = (state) => state.sidebar.filtersData;
export const selectFilterValues = (state) => state.sidebar.filterValues;
export const selectFilterModalStatus = (state) => state.sidebar.openFilterModal;
export const selectFilterTeamInModalStatus = (state) => state.sidebar.showTeamInModal;
export const selectClickedSidefilterType = (state) => state.sidebar.clickedSidefilterType;
export const selectSavedFiltersData = (state) => state.sidebar.filtersData;

const filterKeys = [
  'options',
  'brands',
  'collections',
  'props',
  'is_favourite',
  'is_archive',
  'is_prop_group',
  'is_user_model_uploaded',
  'assign_to_all_org'
];

const syncFilterValues = (filterValues) => {
  const params = filterToParams(filterValues);

  const urlParams = new URLSearchParams(location.search);

  // clear url from previous values

  filterKeys.forEach((key) => {
    urlParams.delete(key);
  });

  const list = [];
  urlParams.forEach((value, key) => {
    if (key.startsWith('fl_')) {
      list.push(key);
    }
  });

  list.forEach((key) => {
    urlParams.delete(key);
  });

  // add new filter values
  Object.entries(params).forEach(([key, value]) => {
    urlParams.append(key, value);
  });

  syncFiltersIntoQuery(urlParams);
};

export function getFilterValuesFromSearchQuery(search) {
  const urlParams = new URLSearchParams(search);

  const values = [];
  urlParams.forEach((value, key) => {
    const [name, id] = key.split('_');
    const keyValues = value.split(',');

    if (key === 'is_favourite') {
      values.push('favourite');
      return;
    }

    if (key === 'is_archive') {
      values.push('archive');
      return;
    }

    if (key === 'is_prop_group') {
      values.push(FILTER.GROUP_PROP);
      return;
    }

    if (key === 'is_user_model_uploaded') {
      values.push(FILTER.USER_MODEL_UPLOADED);
      return;
    }

    if (key === 'assign_to_all_org') {
      values.push(FILTER.ALL_USER_PROPS);
      return;
    }

    if (key === 'is_mattress_material') {
      values.push(FILTER.MATTRESS_BUILDER);
      return;
    }

    if (name === 'brands') {
      keyValues.forEach((v) => {
        values.push(`brand_${v}`);
      });
      return;
    }

    if (name === 'collections') {
      keyValues.forEach((v) => {
        values.push(`collection_${v}`);
      });
      return;
    }

    if (name === 'props') {
      keyValues.forEach((v) => {
        values.push(`prop_${v}`);
      });
      return;
    }

    if (name === 'fl') {
      keyValues.forEach((v) => {
        values.push(`option_${id}_${v}`);
      });
      return;
    }
  });

  return values;
}